<template>
  <div>
    <div class="timezone">
      <span class="timezone-label">Timezone:</span>
      <span class="timezone-text">{{ timezone }}</span>
    </div>

    <business-hours-row
      v-for="(businessHoursRow, index) in openingTimes"
      :key="index"
      v-model="openingTimes[index]"
      :options="options"
      :show-delete-icon="showDeleteIcon"
      :holidays="holidays"
      @update:model-value="onChange"
      @delete="deleteOfficeHours(index)"
    />

    <div class="error-message" v-if="$v.openingTimes.hasNoOverlap.$invalid">{{ overlapErrorMessage }}</div>
    <div class="error-message" v-if="$v.openingTimes.hasAtLeastOneOnlineTime.$invalid">
      {{ atLeastOneOnlineTimeErrorMessage }}
    </div>

    <div class="field">
      <n-button @click="addOfficeHours()" variant="ghost" icon="add">Add Times</n-button>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { BusinessHoursViewModel } from '@/features/bots/models';
import { BusinessHoursOption, BusinessHoursOptions, DayRange } from '.';
import { helpers } from '@vuelidate/validators';
import { HolidayViewModel } from '@/features/bots/models/holidayViewModel';
import { isSameDay, hasOverlappingOpenClose, hasOverlappingHours, hasAtLeastOneOnlineTime } from './validationHelpers';
import { computed, onMounted, PropType, ref, watch } from 'vue';
import { useStore } from 'vuex';
import useVuelidate from '@vuelidate/core';
import { useVModel } from '@vueuse/core';
import BusinessHoursRow from './BusinessHoursRow.vue';

const timezone = 'Europe/London';
const overlapErrorMessage = 'Must not contain conflicting business hours for the same day';
const atLeastOneOnlineTimeErrorMessage = 'Default Business Hours must be online for at least one day of the week';

const hasNoOverlap = () =>
  helpers.withParams({ type: 'hasNoOverlap' }, (value: BusinessHoursOption[]) => {
    for (let i = 0; i < value.length; i++) {
      for (let j = i + 1; j < value.length; j++) {
        const current = value[i];
        const comparison = value[j];
        if (!isSameDay(current, comparison)) continue;
        if (hasOverlappingOpenClose(current, comparison) || hasOverlappingHours(current, comparison)) {
          return false;
        }
      }
    }
    return true;
  });

const hasOnlineTimes = () =>
  helpers.withParams(
    { type: 'hasOnlineTimes' },
    (value: BusinessHoursOption[], parentVm: any) => !parentVm.enforceOnlineDayOfWeek || hasAtLeastOneOnlineTime(value),
  );

// Props and Emits
const props = defineProps({
  modelValue: { type: Array as PropType<BusinessHoursViewModel[]>, required: true },
  allowEmpty: { type: Boolean, default: false },
  enforceOnlineDayOfWeek: { type: Boolean, default: false },
});

const emit = defineEmits<{
  (e: 'update:modelValue', value: BusinessHoursViewModel[]): void;
  (e: 'error', isInvalid: boolean): void;
}>();

const value = useVModel(props, 'modelValue', emit);
const openingTimes = ref<BusinessHoursOption[]>([]);

const rules = {
  openingTimes: {
    hasAtLeastOneOnlineTime: hasOnlineTimes(),
    hasNoOverlap: hasNoOverlap(),
  },
};

const $v = useVuelidate(rules, { openingTimes });

// Store and computed properties
const store = useStore();
const holidays = computed<HolidayViewModel[]>(() => store.getters.holidays);

const options = computed<DayRange[]>(() => {
  const opts = [
    { name: 'Select...', value: null, disabled: true },
    ...BusinessHoursOptions.dayOptions,
    { name: '──────────', value: 'divider-days', disabled: true },
    ...BusinessHoursOptions.dayRangeOptions,
  ];
  if (holidays.value.length > 0) {
    opts.push(
      { name: '──────────', value: 'divider-holidays', disabled: true },
      ...holidays.value.map((h: HolidayViewModel) => ({ name: h.name, holidayId: h.id })),
    );
  }
  return opts;
});

const showDeleteIcon = computed(() => openingTimes.value.length > 1 || props.allowEmpty);

// Lifecycle and methods
onMounted(() => {
  openingTimes.value = value.value.map((vm: BusinessHoursViewModel) => BusinessHoursOption.fromViewModel(vm));
  $v.value.$touch(); // Ensure validation state is initialized
});

function addOfficeHours() {
  openingTimes.value.push(BusinessHoursOptions.getDefaultOption());
  onChange();
}

function deleteOfficeHours(index: number) {
  openingTimes.value.splice(index, 1);
  onChange();
}

function onChange() {
  $v.value.$touch();
  emit('error', $v.value.$invalid);
  value.value = openingTimes.value.map((r: BusinessHoursOption) => r.toViewModel());
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped lang="scss">
.selectSeparator {
  font-size: 1rem;
  color: #ccc;
}

.openingTimes-row {
  display: flex;
  .opening-times-inputs {
    display: flex;
    flex: 1 1 auto;
    align-items: flex-start;
    min-width: 0;
  }
  .control {
    &:not(:last-child) {
      margin-right: 0.5em;
    }
    &.select-input {
      flex: 0 0 30%;
      @media screen and (min-width: 1600px) {
        flex: 0 0 35%;
      }
    }
  }

  .toggle-container {
    display: flex;
    align-items: center;
    margin: 0 1.5rem 0 1rem;
    .control {
      margin-right: 0;
    }
    .toggle-label {
      &__text {
        &--online {
          display: none;
          @media screen and (min-width: 1600px) {
            display: inline;
          }
        }
      }
    }
  }

  .form-field {
    margin-bottom: 0px;
    &:not(:first-of-type) {
      padding-left: 0.5em;
    }
  }

  .time-inputs {
    display: flex;
    align-items: flex-start;
  }
  .time-input {
    max-width: 65px;
  }
}

.timezone {
  margin-bottom: 12px;

  .timezone-label {
    opacity: 0.6;
    margin-right: 0.25rem;
  }

  .timezone-text {
    opacity: 0.8;
  }
}

.with-icon {
  .add-icon {
    width: 1.25em;
    height: 1.25em;
    fill: #c7cdd3;
    margin-left: 0.5em;
    cursor: pointer;

    &:hover {
      fill: #a4a6a9;
    }
  }

  &.disabled {
    .add-icon {
      cursor: default;
      fill-opacity: 0.5;

      &:hover {
        fill: #c7cdd3;
      }
    }
  }
}

.inline-item {
  display: flex;
  align-items: center;
  height: 2.25rem;
}

.error-message {
  color: #ff3860;
  font-size: 0.75rem;
  margin-bottom: 0.75rem;
}
</style>
